import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import LeadsForm from '../Leads/components/LeadsForm';
import AdvancedFilterPanel, { FilterButton } from '../../components/common/AdvancedFilter';
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import { getActivePeriodRange, getActiveFilterCount } from '../../components/common/AdvancedFilter/periodUtils';
import { useLeadsForm } from '../Leads/hooks/useLeadsForm';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import api from '../../api';
import Alert from '../../components/Alert/Alert';
import CompanyEdit from '../Company/components/CompanyEdit/CompanyEdit';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { useAuth } from '../../contexts/AuthProvider';
import * as XLSX from 'xlsx';
import useModalEffect from '../../hooks/useModalEffect';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';
import { buildAuditModeLeadOptions } from './auditClientFilters';

const DEFAULT_PAGE_SIZE = 10;

const getEmptyClientFilters = () => ({
  dateFrom: '',
  dateTo: '',
  createdBy: [],
  stage: '',
  sector: [],
  source: [],
  service: [],
  department: [],
  branchId: [],
  taggingIds: [],
  standard: '',
  period: '',
  startDate: '',
  endDate: '',
  filterType: '',
  assignee: '',
  hasContract: '',
});

const CLIENT_SORT_KEY_TO_BACKEND = {
  companyName: 'companyName',
  projectCount: 'projectCount',
  leadsCount: 'leadsCount',
  contactName: 'contactName',
  phone: 'phone',
  email: 'email',
  sector: 'sectorName',
  createdBy: 'createdByName',
  departmentName: 'departmentName',
  companyCreatedDate: 'companyCreatedDate',
  lastUpdateDate: 'companyUpdatedDate',
};

const Clients = ({ auditMode = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clientData, setClientData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const companyRoutePrefix = auditMode ? '/audit/clients' : '/clients';
  const searchPlaceholder = auditMode ? 'Search audit client, contact, sector' : 'Search Client';
  const pageDescription = auditMode
    ? 'Audit Platform shows only clients with Certification Program or Sustainability audit projects.'
    : 'CRM client list for all services, including Certification Program, Sustainability, Training Program, Audit Program, and Advisory.';
  const [appliedSearch, setAppliedSearch] = useState('');
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [showPassword, setShowPassword] = useState([]);
  const [showConfirmPassword, setShowConfirmPassword] = useState([]);

  const { user, can, capabilitiesLoading } = useAuth();

  const canClient = (capabilityKey) => can(capabilityKey, 'client');

  const formatDateDisplay = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const togglePasswordVisibility = (index) => {
    setShowPassword(prev => {
      const copy = [...prev];
      copy[index] = !copy[index];
      return copy;
    });
  };

  const toggleConfirmPasswordVisibility = (index) => {
    setShowConfirmPassword(prev => {
      const copy = [...prev];
      copy[index] = !copy[index];
      return copy;
    });
  };

  const [partnerTypes, setPartnerTypes] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [userBranchesForFilter, setUserBranchesForFilter] = useState([]);
  const [taggingOptions, setTaggingOptions] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);

  const [filterData, setFilterData] = useState(getEmptyClientFilters);
  const [appliedFilters, setAppliedFilters] = useState(getEmptyClientFilters);

  const [filterOptions, setFilterOptions] = useState({
    createdByUsers: [],
    stages: [],
    sectors: [],
    sources: [],
    services: [],
    departments: [],
    assignees: []
  });

  // Raw internal users with departmentId for cascade filtering
  const [allInternalUsers, setAllInternalUsers] = useState([]);

  const navigate = useNavigate();
  const { loadPersistedState, persistState } = usePersistedFilters(auditMode ? 'audit_clients' : 'clients');

  const {
    formData,
    formErrors,
    serviceSections,
    uploadedFiles,
    handleInputChange,
    handleTextChange,
    handleFormSubmit,
    addServiceSection,
    deleteServiceSection,
    onDrop,
    removeFile,
    resetForm,
    options,
    fetchContactOptions,
    standardsOptionsMap
  } = useLeadsForm();

  const leadFormOptions = useMemo(
    () => buildAuditModeLeadOptions(options, auditMode),
    [options, auditMode]
  );

  const [companyFormData, setCompanyFormData] = useState({
    branchId: '',
    contacts: [],
  });
  const [companyErrors, setCompanyErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [modalDataLoading, setModalDataLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const handleCompanyInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyFormData(prev => ({ ...prev, [name]: value }));
    if (['phone', 'website', 'companyEmail', 'npwp', 'country', 'state', 'city', 'postalCode'].includes(name)) {
      setCompanyErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCompanySelectChange = (name, value) => {
    if (name === 'taggingIds') {
      const arr = Array.isArray(value) ? value : (value ? [value] : []);
      const normalizedIds = arr.map(v => (v != null && v !== '') ? String(v) : null).filter(Boolean);
      setCompanyFormData(prev => ({ ...prev, [name]: normalizedIds }));
      if (normalizedIds.length > 0) setCompanyErrors(prev => ({ ...prev, [name]: undefined }));
      return;
    }
    setCompanyFormData(prev => ({ ...prev, [name]: value }));
    if (value) {
      setCompanyErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const addContact = () => {
    setCompanyFormData(prev => ({
      ...prev,
      contacts: [...(prev.contacts || []), { firstName: '', lastName: '', email: '', phone: '', position: '' }]
    }));
  };

  const removeContact = (index) => {
    setCompanyFormData(prev => ({
      ...prev,
      contacts: (Array.isArray(prev.contacts) ? prev.contacts : []).filter((_, i) => i !== index)
    }));
  };

  const getClientListFilters = (filters = appliedFilters, search = appliedSearch) => {
    const q = (search ?? '').trim();
    if (q) return { ...filters, search: q };
    return filters;
  };

  const fetchClients = async (filters = {}, pageNum = page, pageSize = size, sortConfigOverride = null) => {
    setTableLoading(true);
    setOverviewLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(pageNum));
      params.set('size', String(pageSize));

      const effectiveDateFrom = filters.startDate || filters.dateFrom;
      const effectiveDateTo = filters.endDate || filters.dateTo;
      if (effectiveDateFrom) params.append('dateFrom', effectiveDateFrom);
      if (effectiveDateTo) params.append('dateTo', effectiveDateTo);

      if (Array.isArray(filters.branchId) && filters.branchId.length > 0) {
        filters.branchId
          .map(v => String(v).trim())
          .filter(Boolean)
          .forEach(v => params.append('branchId', v));
      }

      if (Array.isArray(filters.taggingIds) && filters.taggingIds.length > 0) {
        params.append('taggingIds', filters.taggingIds.map(v => String(v)).join(','));
      }

      ['stage', 'standard', 'filterType', 'search', 'assignee', 'hasContract'].forEach((key) => {
        const value = filters[key];
        if (value && value !== '' && value !== 'all') {
          params.append(key, value);
        }
      });
      ['createdBy', 'sector', 'source', 'service', 'department'].forEach((key) => {
        const value = filters[key];
        if (Array.isArray(value) && value.length > 0) {
          params.append(key, value.map(v => String(v)).join(','));
        } else if (value && !Array.isArray(value) && value !== '' && value !== 'all') {
          params.append(key, value);
        }
      });

      if (auditMode) {
        params.append('hasAuditProject', 'true');
      }

      if (!canClient('view_global')) {
        if (canClient('view_department')) {
          if (!params.has('createdBy') && user?.departmentId) {
            params.append('viewDepartmentId', user.departmentId);
          }
        } else if (canClient('view_own')) {
          if (!params.has('createdBy') && user?.id) {
            params.append('createdBy', user.id);
          }
        }
      }

      const effectiveSort = sortConfigOverride ?? sortConfig;
      if (effectiveSort?.key && CLIENT_SORT_KEY_TO_BACKEND[effectiveSort.key]) {
        const backendSortProperty = CLIENT_SORT_KEY_TO_BACKEND[effectiveSort.key];
        params.set('sort', `${backendSortProperty},${effectiveSort.direction}`);
      }

      // ClientController only exposes /filter (no bare list endpoint), so always use it
      const baseUrl = `/crm/client/filter?${params.toString()}`;

      const res = await api.get(baseUrl);
      const data = res.data;
      const list = Array.isArray(data) ? data : (data?.content ?? []);
      setClientData(list);
      if (data && typeof data.totalElements === 'number') {
        setTotalElements(data.totalElements);
        setTotalPages(data.totalPages ?? Math.max(1, Math.ceil(data.totalElements / pageSize)));
      } else {
        setTotalElements(list.length);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
      setClientData([]);
      setTotalElements(0);
      setTotalPages(0);
      const message = err?.response?.data?.message ?? err?.message ?? 'Failed to load clients.';
      setAlert({ show: true, type: 'danger', message });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
    } finally {
      setTableLoading(false);
      setOverviewLoading(false);
    }
  };

  useEffect(() => {
    if (!capabilitiesLoading) {
      setTableLoading(true);
      setOverviewLoading(true);

      const saved = loadPersistedState();
      if (saved) {
        const restoredFilters = saved.filters ?? getEmptyClientFilters();
        const restoredSearch = saved.search ?? '';
        const restoredPage = saved.page ?? 0;
        const restoredSize = saved.size ?? DEFAULT_PAGE_SIZE;

        setAppliedFilters(restoredFilters);
        setFilterData(restoredFilters);
        setAppliedSearch(restoredSearch);
        setSearchQuery(restoredSearch);
        setPage(restoredPage);
        setSize(restoredSize);

        fetchClients(
          getClientListFilters(restoredFilters, restoredSearch),
          restoredPage,
          restoredSize,
          sortConfig
        );
      } else {
        fetchClients(getClientListFilters(), page, size, sortConfig);
      }
    }
    // auditMode is a dependency, not just capabilitiesLoading: App.jsx routes
    // /clients and /audit/clients to the same <Clients> element type at the
    // same tree position, so React Router re-renders this instance instead
    // of unmounting it when navigating between them. Without auditMode here,
    // this effect never re-ran on that navigation and the stale list (with
    // the previous hasAuditProject filter) stayed on screen until a manual
    // refresh. See ClientsList.auditModeSwitch.test.jsx.
  }, [capabilitiesLoading, auditMode]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [partnerTypeRes, entityTypeRes, sectorRes, taggingRes, filterOptionsRes, createdByUsersRes, currencyRes, branchesRes, leadStatusesRes, mdSourcesRes, mdDepartmentsRes, internalUsersRes, servicesRes] = await Promise.all([
          api.get('/md/business-partner-type'),
          api.get('/md/entity-type'),
          api.get('/md/sector'),
          api.get('/md/tagging', { params: { moduleKey: 'companies' } }),
          api.get('/crm/prospects/filter-options').catch(() => ({ data: { createdByUsers: [], stages: [], sources: [], departments: [] } })),
          api.get('/crm/prospects/created-by-users').catch(() => ({ data: [] })),
          api.get('/crm/currencies'),
          api.get('/branches'),
          api.get('/md/lead-status').catch(() => ({ data: [] })),
          api.get('/md/company-source').catch(() => ({ data: [] })),
          api.get('/md/departement').catch(() => ({ data: [] })),
          api.get('/md/internal-users/option-internal-user').catch(() => ({ data: [] })),
          api.get('/crm/services/options').catch(() => ({ data: [] }))
        ]);
        setPartnerTypes(partnerTypeRes.data);
        setEntityTypes(entityTypeRes.data);
        setSectors(sectorRes.data);
        setTaggingOptions(taggingRes.data || []);
        setCurrencies(currencyRes.data || []);
        const branchData = branchesRes.data?.data ?? branchesRes.data ?? [];
        setBranches(Array.isArray(branchData) ? branchData : []);

        const filterOptionsData = filterOptionsRes.data || { createdByUsers: [], stages: [], sources: [], departments: [] };
        const rawCreatedBy = createdByUsersRes.data?.length > 0
          ? createdByUsersRes.data
          : (filterOptionsData.createdByUsers || []);

        // Map same as Leads: ensure id field is set explicitly, prefer firstName+lastName
        const createdByUsers = rawCreatedBy.map(u => {
          const displayName = [u.firstName, u.lastName].filter(Boolean).join(' ')
            || u.fullName || u.full_name || u.name || u.label || u.username || '';
          return {
            id: u.id,
            value: u.id,
            name: displayName,
            label: displayName,
            email: u.email,
            firstName: u.firstName,
            lastName: u.lastName
          };
        });

        setFilterOptions({
          createdByUsers,
          stages: (leadStatusesRes.data || []).map(s => ({ id: s.name, name: s.name })),
          sectors: (sectorRes.data || []).map(s => ({ value: s.id, label: s.name })),
          sources: (mdSourcesRes.data || []).map(s => ({ id: s.id, name: s.name })),
          services: (servicesRes.data || []).map(s => ({ value: s.id ?? s.value, label: s.name ?? s.label })),
          departments: (mdDepartmentsRes.data || []).map(d => ({ id: d.id, name: d.name })),
          // Sales Owner ("assignee" on backend) — lets Sales monitor just their own clients.
          assignees: (filterOptionsData.assignees || []).map(a => ({ value: String(a.id ?? a.value), label: a.label ?? a.name }))
        });
        setAllInternalUsers(internalUsersRes.data || []);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    const loadUserBranches = async () => {
      try {
        const res = await api.get(`/user-branches/user/${user.id}`);
        const data = res.data?.data ?? res.data ?? [];
        setUserBranchesForFilter(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching user branches for filter:', err);
        setUserBranchesForFilter([]);
      }
    };
    loadUserBranches();
  }, [user?.id]);

  const handleAddContact = () => {
    setCompanyFormData(prev => ({
      ...prev,
      contacts: [
        ...(Array.isArray(prev.contacts) ? prev.contacts : []),
        {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          position: '',
          allowLogin: false,
          generatePassword: false,
          password: '',
          confirmPassword: ''
        }
      ]
    }));
  };

  const handleContactInputChange = (index, field, value) => {
    const baseContacts = Array.isArray(companyFormData?.contacts) ? companyFormData.contacts : [];
    const updatedContacts = [...baseContacts];
    const existing = updatedContacts[index] ?? {};
    updatedContacts[index] = { ...existing, [field]: value };
    setCompanyFormData(prev => ({ ...prev, contacts: updatedContacts }));
  };


  const handleAddNew = (client) => {
    if (!canClient('create')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to create leads.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }
    resetForm();
    setTimeout(() => {
      if (client) {
        const companyId = client.id || client.companyId || client._id || client.ID;
        if (companyId) {
          handleInputChange('company', String(companyId));
          handleInputChange('companyWebsite', client.website || '');
          handleInputChange('companyPhoneNumber', client.contactNumber || client.phone || '');
          handleInputChange('address', client.address || '');
          handleInputChange('sector', client.partnerType || client.sectorName || '');
          handleInputChange('contact', client.pic || client.contactName || '');
          handleInputChange('contactPhoneNumber', client.contactNumber || client.phone || '');
          handleInputChange('email', client.email || '');
          handleInputChange('stage', 'New');
          handleInputChange('projectName', '');
          handleInputChange('projectDescription', '');
          handleInputChange('leadSource', client.leadSource || '');
          fetchContactOptions(String(companyId));
        }
      }
      setIsModalOpen(true);
    }, 100);
  };

  const handleModalClose = () => setIsModalOpen(false);

  const handleCompanyClick = (companyId) => navigate(`${companyRoutePrefix}/${companyId}`);

  const handleSearch = () => {
    setPage(0);
    const q = (searchQuery ?? '').trim();
    setAppliedSearch(q);
    const searchFilters = getClientListFilters(appliedFilters, q);
    persistState({ filters: appliedFilters, search: q, page: 0, size });
    fetchClients(searchFilters, 0, size, sortConfig);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const transformedData = clientData.map(item => ({
    ...item,
    projectCount: item.projectCount || 0,
    leadsCount: item.leadsCount || 0,
    contactName: item.contactName,
    phone: item.phone,
    sector: item.sectorName,
    email: item.email,
    assignedUser: item.assignedUserName || 'Unassigned',
    tagging: item.tagging ?? item.company?.tagging ??
      (item.taggings && Array.isArray(item.taggings) ? item.taggings.map(t => t.name).join(', ') : null) ??
      (item.company?.taggings && Array.isArray(item.company.taggings) ? item.company.taggings.map(t => t.name).join(', ') : null) ?? '-',
    createdBy: item.createdByName || item.createdBy || item.userName || item.user?.name ||
      item.createdUser?.name || item.createdByUser?.name || item.companyCreatedBy || item.createdByUserName || '-',
    departmentName: item.departmentName || '-',
    companyCreatedDate: item.companyCreatedDate || '-',
    lastUpdateDate: item.lastUpdateDate || item.companyUpdatedDate || item.udate || '-',
    lastUpdateBy: item.lastUpdateByName || item.lastUpdateBy || item.updatedByName || item.updatedBy ||
      item.updateUserName || item.updateUser?.name || item.updatedUser?.name || item.lastUpdatedByUserName || '-',
    contractStatus: item.contractStatus || null,
    hasContract: Boolean(item.hasContract),
  }));

  const columns = [
    {
      key: 'companyName',
      label: 'Company Name',
      sortable: true,
      className: 'text-truncate',
      render: (value, row) => (
        <a
          href={`/clients/${row.companyId}`}
          className="text-primary text-truncate d-inline-block text-decoration-none"
          style={{ cursor: 'pointer', maxWidth: '250px', width: '250px' }}
          onClick={(e) => {
            if (e.ctrlKey || e.metaKey || e.button === 1) return;
            e.preventDefault();
            handleCompanyClick(row.companyId);
          }}
          onAuxClick={(e) => { if (e.button === 1) return; }}
          title={value}
          aria-label={`View details for ${value}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleCompanyClick(row.companyId);
            }
          }}
        >
          {value}
        </a>
      )
    },
    {
      key: 'tagging',
      label: 'Tagging',
      sortable: false,
      className: 'text-truncate',
      render: (value) => {
        const raw = !value ? '' : (typeof value === 'string' ? value : (typeof value === 'object' && value && value.name ? value.name : (taggingOptions.find(opt => String(opt.id) === String(value))?.name ?? value)));
        const tags = raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : [];
        if (tags.length === 0) return <span className="text-muted">-</span>;
        return (
          <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
            {tags.map((tag, i) => (
              <span key={i} className="badge bg-blue-lt text-wrap" title={tag} style={{ fontSize: '0.7rem' }}>
                {tag}
              </span>
            ))}
          </span>
        );
      }
    },
    {
      key: 'projectCount',
      label: auditMode ? 'Audit Projects' : 'Projects',
      type: 'number',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: '80px', width: '80px' }} title={value}>
          {value}
        </span>
      )
    },
    {
      key: 'leadsCount',
      label: 'Leads',
      type: 'number',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: '80px', width: '80px' }} title={value}>
          {value}
        </span>
      )
    },
    {
      key: 'contactName',
      label: 'Contact Name',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: '200px', width: '200px' }} title={value}>
          {value}
        </span>
      )
    },
    {
      key: 'phone',
      label: 'Contact No.',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: '150px', width: '150px' }} title={value}>
          {value}
        </span>
      )
    },
    {
      key: 'email',
      label: 'Contact Email',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: '220px', width: '220px' }} title={value}>
          {value}
        </span>
      )
    },
    {
      key: 'sector',
      label: 'Sector',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: '180px', width: '180px' }} title={value}>
          {value}
        </span>
      )
    },
    {
      key: 'createdBy',
      label: 'Created By',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: '160px', width: '160px' }} title={value}>
          {value}
        </span>
      )
    },
    {
      key: 'departmentName',
      label: 'Department',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: '150px', width: '150px' }} title={value}>
          {value}
        </span>
      )
    },
    {
      key: 'companyCreatedDate',
      label: 'Create Date',
      type: 'date',
      sortable: true,
      className: 'text-truncate',
      render: (value) => {
        if (!value || value === '-') return '-';
        const dateValue = formatDateDisplay(value);
        return (
          <span className="text-truncate d-inline-block" style={{ maxWidth: '140px', width: '140px' }} title={dateValue}>
            {dateValue || '-'}
          </span>
        );
      }
    },
    {
      key: 'lastUpdateDate',
      label: 'Last Update',
      type: 'date',
      sortable: true,
      className: 'text-truncate',
      render: (value) => {
        if (!value || value === '-') return '-';
        const dateValue = formatDateDisplay(value);
        return (
          <span className="text-truncate d-inline-block" style={{ maxWidth: '140px', width: '140px' }} title={dateValue}>
            {dateValue || '-'}
          </span>
        );
      }
    },
    {
      key: 'lastUpdateBy',
      label: 'Update By',
      sortable: false,
      className: 'text-truncate',
      render: (value) => {
        const displayValue = value || '-';
        return (
          <span className="text-truncate d-inline-block" style={{ maxWidth: '160px', width: '160px' }} title={displayValue}>
            {displayValue}
          </span>
        );
      }
    }
  ];

  const auditColumns = columns.filter(column => [
    'companyName',
    'projectCount',
    'contactName',
    'phone',
    'email',
    'sector',
    'lastUpdateDate'
  ].includes(column.key));

  // Build userId → { deptId, status } lookup — same pattern as Leads
  const clientUserDeptLookup = useMemo(() => {
    const map = new Map();
    (allInternalUsers || []).forEach(u => {
      const deptId = u.departmentId || u.departementId;
      if (u.id != null) map.set(String(u.id), { deptId: deptId != null ? String(deptId) : null, status: u.status });
    });
    return map;
  }, [allInternalUsers]);

  // Append " (Inactive)" for status "0" — same pattern as Leads
  const withInactiveBadge = (userId, label) => {
    const entry = clientUserDeptLookup.get(String(userId));
    return entry?.status === '0' ? `${label} (Inactive)` : label;
  };

  // Cascade: filter Created By by selected department — same pattern as Leads
  const cascadedCreatedByOptions = useMemo(() => {
    return (filterOptions.createdByUsers || []).map(u => ({
      value: String(u.id),
      label: withInactiveBadge(u.id, u.label || u.name)
    }));
  }, [filterOptions.createdByUsers]);

  const clientFilterFields = [
    { key: 'assignee', label: 'Sales Owner', type: 'select', options: filterOptions.assignees || [] },
    { key: 'hasContract', label: 'Status Kontrak', type: 'select', options: [
      { value: 'true', label: 'Ada Kontrak' },
      { value: 'false', label: 'Butuh Kontrak' },
    ] },
    { key: 'department', label: 'Department', type: 'multiselect', options: (filterOptions.departments || []).map(d => ({ value: d.id, label: d.name })) },
    { key: 'createdBy', label: 'Created By', type: 'multiselect', options: cascadedCreatedByOptions },
    { key: 'sector', label: 'Sector', type: 'multiselect', options: filterOptions.sectors || [] },
    { key: 'source', label: 'Source', type: 'multiselect', options: (filterOptions.sources || []).map(s => ({ value: s.id, label: s.name })) },
    { key: 'service', label: 'Service', type: 'multiselect', options: filterOptions.services || [] },
    { key: 'branchId', label: 'Branch', type: 'multiselect', options: (userBranchesForFilter || []).map(b => ({ value: String(b.id), label: b.name })) },
    { key: 'taggingIds', label: 'Tagging', type: 'multiselect', options: (taggingOptions || []).map(t => ({ value: String(t.id), label: t.name })) },
  ];

  const activeFilterCount = getActiveFilterCount(appliedFilters, clientFilterFields);
  const clientFilterTags = useMemo(() => {
    const tags = [];

    if (appliedFilters.period) {
      tags.push({
        key: 'period',
        label: `Period: ${String(appliedFilters.period).replace(/_/g, ' ')}`,
      });
    } else if (appliedFilters.startDate || appliedFilters.endDate) {
      tags.push({
        key: 'period',
        label: `Period: ${[appliedFilters.startDate, appliedFilters.endDate].filter(Boolean).join(' - ')}`,
      });
    }

    clientFilterFields.forEach((field) => {
      if (field.type === 'dateRange') {
        return;
      }
      const value = appliedFilters[field.key];
      const isEmpty = value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0);
      if (isEmpty) {
        return;
      }

      if (Array.isArray(value)) {
        tags.push({ key: field.key, label: `${field.label}: ${value.length} selected` });
        return;
      }

      const option = (field.options || []).find((opt) => String(opt[field.valueField || 'value']) === String(value));
      tags.push({ key: field.key, label: `${field.label}: ${option ? option[field.labelField || 'label'] : value}` });
    });

    return tags;
  }, [appliedFilters, clientFilterFields]);

  const getClearedClientFilters = (filters, key) => {
    const keysToClear = ['period', 'startDate', 'endDate'].includes(key)
      ? ['period', 'startDate', 'endDate']
      : [key];
    const nextFilters = { ...filters };
    keysToClear.forEach((fieldKey) => {
      nextFilters[fieldKey] = ['branchId', 'taggingIds', 'department', 'createdBy', 'sector', 'source', 'service'].includes(fieldKey) ? [] : '';
    });
    return nextFilters;
  };

  const handleFilterChange = (name, value) => {
    if (['branchId', 'taggingIds', 'department', 'createdBy', 'sector', 'source', 'service'].includes(name)) {
      const arr = Array.isArray(value) ? value : (value ? [value] : []);
      setFilterData(prev => ({ ...prev, [name]: arr }));
      return;
    }
    setFilterData(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterReset = () => {
    const emptyFilters = getEmptyClientFilters();
    setFilterData(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSearchQuery('');
    setAppliedSearch('');
    setPage(0);
    persistState({ filters: emptyFilters, search: '', page: 0, size });
    fetchClients({}, 0, size, sortConfig);
  };

  const handleFilterApply = () => {
    const { startDate, endDate } = getActivePeriodRange(filterData);
    const applied = { ...filterData, startDate, endDate };
    setAppliedFilters(applied);
    setPage(0);
    persistState({ filters: applied, search: appliedSearch, page: 0, size });
    fetchClients(getClientListFilters(applied, appliedSearch), 0, size, sortConfig);
  };

  const handleClientChipRemove = (key) => {
    const keysToCheck = ['period', 'startDate', 'endDate'].includes(key)
      ? ['period', 'startDate', 'endDate']
      : [key];
    const hasActiveValue = keysToCheck.some((fieldKey) => {
      const value = appliedFilters[fieldKey];
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    });
    if (!hasActiveValue) return;

    const nextAppliedFilters = getClearedClientFilters(appliedFilters, key);
    setFilterData(prev => getClearedClientFilters(prev, key));
    setAppliedFilters(nextAppliedFilters);
    setPage(0);
    persistState({ filters: nextAppliedFilters, search: appliedSearch, page: 0, size });
    fetchClients(getClientListFilters(nextAppliedFilters, appliedSearch), 0, size, sortConfig);
  };

  const handleEditLead = (client) => {
    if (!canClient('edit')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to edit.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }
    fetchCompanyDetail(client.companyId);
  };

  const handleDeleteLead = (client) => {
    if (!canClient('delete')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to delete.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }
    setDeleteTarget(client);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setTableLoading(true);
      await api.delete('/md/companies', { data: [deleteTarget.companyId] });
      fetchClients(getClientListFilters(), page, size, sortConfig);
      setAlert({ show: true, type: 'success', message: 'Client deleted successfully!' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
    } catch (err) {
      console.error('Error deleting client:', err);
      setAlert({ show: true, type: 'danger', message: 'Failed to delete client. Please try again.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
    } finally {
      setTableLoading(false);
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handleCreateNewCompany = () => {
    if (!canClient('create')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to create clients.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }
    setCompanyFormData({
      branchId: '',
      taggingIds: [],
      contacts: [{
        firstName: '', lastName: '', email: '', phone: '', position: '',
        allowLogin: false, generatePassword: false, password: '', confirmPassword: ''
      }]
    });
    setCompanyErrors({});
    setEditingId(null);
    setShowCompanyModal(true);
  };

  const handleExportExcel = () => {
    if (!canClient('export_data')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to export.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }
    try {
      const excelData = transformedData.map(row => ({
        'Company Name': row.companyName || '',
        'Projects': row.projectCount || 0,
        'Leads': row.leadsCount || 0,
        'Contact Name': row.contactName || '',
        'Contact No.': row.phone || '',
        'Contact Email': row.email || '',
        'Sector': row.sector || '',
        'Tagging': row.tagging || '-',
        'Created By': row.createdBy || '',
        'Department': row.departmentName || '',
        'Create Date': formatDateDisplay(row.companyCreatedDate) || '-',
        'Last Update': formatDateDisplay(row.lastUpdateDate) || '-',
        'Update By': row.lastUpdateBy || '-'
      }));
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet['!cols'] = [
        { wch: 30 }, { wch: 10 }, { wch: 10 }, { wch: 25 }, { wch: 15 },
        { wch: 30 }, { wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 20 },
        { wch: 15 }, { wch: 15 }, { wch: 20 }
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients');
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      XLSX.writeFile(workbook, `clients_export_${timestamp}.xlsx`);
      setAlert({ show: true, type: 'success', message: 'Excel file exported successfully!' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
    } catch (e) {
      console.error('Export Excel failed:', e);
      setAlert({ show: true, type: 'danger', message: 'Failed to export Excel file. Please try again.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
    }
  };

  const handleExportPDF = () => {
    if (!canClient('export_data')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to export.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }
    try {
      const win = window.open('', '_blank');
      if (!win) return;
      const style = `<style>body{font-family:Arial,sans-serif;padding:16px}h2{margin:0 0 12px 0}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:8px;text-align:left;font-size:12px}th{background:#f5f5f5}</style>`;
      const header = ['Company Name', 'Projects', 'Leads', 'Contact Name', 'Contact No.', 'Contact Email', 'Sector', 'Tagging', 'Created By', 'Department', 'Create Date'];
      const rows = transformedData.map(r => [
        r.companyName, r.projectCount, r.leadsCount, r.contactName, r.phone, r.email,
        r.sector, r.tagging || '-', r.createdBy, r.departmentName, formatDateDisplay(r.companyCreatedDate) || '-'
      ]);
      const table = `<table><thead><tr>${header.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>${rows.map(cols => `<tr>${cols.map(c => `<td>${c != null ? String(c) : ''}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
      win.document.write(`<!doctype html><html><head><meta charset="utf-8">${style}<title>Client Export</title></head><body><h2>Client Export</h2>${table}</body></html>`);
      win.document.close();
      win.focus();
      win.print();
    } catch (e) {
      console.error('Export PDF failed:', e);
    }
  };

  const handleContactLoginToggle = (index, value) => {
    const baseContacts = Array.isArray(companyFormData?.contacts) ? companyFormData.contacts : [];
    const updatedContacts = [...baseContacts];
    updatedContacts[index] = { ...(updatedContacts[index] ?? {}), allowLogin: value };
    if (!value) {
      updatedContacts[index].generatePassword = false;
      updatedContacts[index].password = '';
      updatedContacts[index].confirmPassword = '';
    }
    setCompanyFormData(prev => ({ ...prev, contacts: updatedContacts }));
  };

  const fetchCompanyDetail = async (id) => {
    try {
      setModalDataLoading(true);
      setShowCompanyModal(true);
      const res = await api.get(`/md/companies/${id}`);
      const data = res.data;
      const companyData = data.company ?? {};
      companyData.contacts = Array.isArray(data?.contacts) ? data.contacts : [];

      if (companyData.taggings && Array.isArray(companyData.taggings)) {
        companyData.taggingIds = companyData.taggings.map(t => String(t.id));
      } else if (companyData.tagging && typeof companyData.tagging === 'string' && taggingOptions.length > 0) {
        const names = companyData.tagging.split(',').map(s => s.trim()).filter(Boolean);
        companyData.taggingIds = names.map(n => {
          const found = taggingOptions.find(opt => opt.name === n || String(opt.id) === n);
          return found ? String(found.id) : null;
        }).filter(Boolean);
      } else {
        companyData.taggingIds = [];
      }
      delete companyData.clientFlagging;
      delete companyData.clientFlaggingId;
      delete companyData.taggings;

      companyData.branchId = companyData.branchId != null ? String(companyData.branchId) : (companyData.branch?.id != null ? String(companyData.branch.id) : '');

      setCompanyFormData(companyData);
      setCompanyErrors({});
      setEditingId(id);
    } catch (error) {
      console.error('Failed to load company details:', error);
      const msg = error?.response?.data?.message ?? error?.message ?? 'Failed to load company details.';
      setAlert({ show: true, type: 'danger', message: msg });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
      setShowCompanyModal(false);
    } finally {
      setModalDataLoading(false);
    }
  };

  const handleSubmitCompany = async (e) => {
    e.preventDefault();
    const isCreating = !editingId;

    if (isCreating && !canClient('create')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to create clients.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }
    if (!isCreating && !canClient('edit')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to update clients.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }
    if (isCreating) {
      const branchId = companyFormData.branchId;
      if (branchId === undefined || branchId === null || String(branchId).trim() === '') {
        setCompanyErrors(prev => ({ ...prev, branchId: 'Branch is required' }));
        return;
      }
    }
    setCompanyErrors({});
    setLoading(true);

    const taggingIdsAsNumbers = (companyFormData.taggingIds && Array.isArray(companyFormData.taggingIds))
      ? companyFormData.taggingIds.map(id => Number(id)).filter(n => !isNaN(n))
      : [];
    const payload = { ...companyFormData, taggingIds: taggingIdsAsNumbers };

    try {
      if (isCreating) {
        await api.post('/md/companies', payload);
        setAlert({ show: true, type: 'success', message: 'Client created successfully!' });
      } else {
        await api.put(`/md/companies/${editingId}`, payload);
        setAlert({ show: true, type: 'success', message: 'Client updated successfully!' });
      }

      setShowCompanyModal(false);
      setCompanyFormData({ branchId: '', contacts: [] });
      setCompanyErrors({});
      fetchClients(getClientListFilters(), page, size, sortConfig);
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
    } catch (error) {
      console.error(`Failed to ${isCreating ? 'create' : 'update'} client:`, error);
      let message = error?.response?.data?.message ?? error?.response?.data?.error;
      if (typeof message !== 'string') message = null;
      if (message && /constraint|ConstraintViolation|could not execute batch|foreign key|fk\d|nested exception/i.test(message)) {
        message = 'This contact cannot be removed because it is linked to one or more leads or other records. Reassign or remove those links first, then try again.';
      }
      setAlert({ show: true, type: 'danger', message: message || `Failed to ${isCreating ? 'create' : 'update'} client. Please try again.` });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-xl">
      {alert.show && (
        <Alert type={alert.type} message={alert.message} onClose={() => setAlert({ show: false, type: '', message: '' })} />
      )}
      <div className="row g-2 align-items-center">
        <div className="col">
          <h2 className="page-title">{auditMode ? 'Audit Client Dashboard' : 'Dashboard Client'}</h2>
          <p className="text-muted mb-0">{pageDescription}</p>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <div className="btn-list d-flex flex-wrap gap-2">
            {canClient('create') && (
              <>
                <a href="#" className="btn btn-outline-primary" onClick={() => handleCreateNewCompany()} aria-label="Create new client">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-2" aria-hidden="true">
                    <path d="M12 5l0 14"></path>
                    <path d="M5 12l14 0"></path>
                  </svg>
                  {auditMode ? 'New Audit Client' : 'New Client'}
                </a>
                <a href="#" className="btn btn-primary" onClick={() => handleAddNew(null)} aria-label="Create new lead">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-2" aria-hidden="true">
                    <path d="M12 5l0 14"></path>
                    <path d="M5 12l14 0"></path>
                  </svg>
                  {auditMode ? 'New Audit Lead' : 'New Lead'}
                </a>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist" aria-label="Client dashboard tabs">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-client-list" className="nav-link active" data-bs-toggle="tab" role="tab" aria-selected="true" aria-controls="tabs-client-list">{auditMode ? 'Audit Client List' : 'List Data'}</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-client-list" role="tabpanel" aria-labelledby="tabs-client-list">
                    <GenericView
                      data={transformedData}
                      columns={auditMode ? auditColumns : columns}
                      onEdit={canClient('edit') ? handleEditLead : undefined}
                      onDelete={canClient('delete') ? handleDeleteLead : undefined}
                      onAdd={canClient('create') ? handleAddNew : undefined}
                      filterComponent={
                        <FilterButton offcanvasId="offcanvasClients" activeCount={activeFilterCount} />
                      }
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          onKeyPress={handleSearchKeyPress}
                          placeholder={searchPlaceholder}
                          id="clients-search"
                          autocomplete="off"
                        />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={canClient('export_data') ? handleExportExcel : undefined}
                          onExportPDF={canClient('export_data') ? handleExportPDF : undefined}
                        />
                      }
                      belowTopControlsContent={
                        <FilterAwarenessBar
                          title={`${auditMode ? 'Audit clients' : 'Clients'} is showing a filtered view`}
                          summary={`Filtered by ${activeFilterCount} ${activeFilterCount === 1 ? 'criterion' : 'criteria'}`}
                          detail={clientFilterTags.map((tag) => tag.label).join(' · ')}
                          loading={tableLoading || capabilitiesLoading}
                          activeCount={activeFilterCount}
                          filterTags={clientFilterTags}
                          onRemoveFilter={handleClientChipRemove}
                          onResetFilters={handleFilterReset}
                        />
                      }
                      checkboxSelection={true}
                      onRowClick={() => {}}
                      emptyStateMessage={auditMode ? 'No audit clients found. Clients appear here after audit-relevant project, certificate, document, or configuration data exists.' : 'No clients found'}
                      isLoading={tableLoading || capabilitiesLoading}
                      pagination={{
                        currentPage: page + 1,
                        totalRows: totalElements,
                        rowsPerPage: size,
                        onPageChange: (newPage) => {
                          const nextPage = newPage - 1;
                          setPage(nextPage);
                          persistState({ filters: appliedFilters, search: appliedSearch, page: nextPage, size });
                          fetchClients(getClientListFilters(), nextPage, size, sortConfig);
                        },
                        onRowsPerPageChange: (newSize) => {
                          setSize(newSize);
                          setPage(0);
                          persistState({ filters: appliedFilters, search: appliedSearch, page: 0, size: newSize });
                          fetchClients(getClientListFilters(), 0, newSize, sortConfig);
                        },
                      }}
                      onSortChange={(newSortConfig) => {
                        setSortConfig(newSortConfig);
                        setPage(0);
                        fetchClients(getClientListFilters(), 0, size, newSortConfig);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Offcanvas */}
      <AdvancedFilterPanel
        offcanvasId="offcanvasClients"
        title="Advanced Filter"
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={clientFilterFields}
      />

      <LeadsForm
        formData={formData}
        formErrors={formErrors}
        handleInputChange={handleInputChange}
        handleTextChange={handleTextChange}
        handleFormSubmit={handleFormSubmit}
        serviceSections={serviceSections}
        addServiceSection={addServiceSection}
        deleteServiceSection={deleteServiceSection}
        uploadedFiles={uploadedFiles}
        onDrop={onDrop}
        removeFile={removeFile}
        options={leadFormOptions}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        fetchContactOptions={fetchContactOptions}
        standardsOptionsMap={standardsOptionsMap}
        onSuccessSubmit={(error) => {
          if (error) {
            setAlert({ show: true, type: 'danger', message: 'Failed to create lead. Please try again.' });
          } else {
            setAlert({ show: true, type: 'success', message: 'New lead created successfully!' });
            setPage(0);
            fetchClients(getClientListFilters(), 0, size, sortConfig);
          }
          setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
          setIsModalOpen(false);
        }}
      />

      <CompanyEdit
        isOpen={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        onSubmit={handleSubmitCompany}
        editingId={editingId}
        companyFormData={companyFormData}
        companyErrors={companyErrors}
        loading={loading}
        modalDataLoading={modalDataLoading}
        handleCompanyInputChange={handleCompanyInputChange}
        handleCompanySelectChange={handleCompanySelectChange}
        handleContactInputChange={handleContactInputChange}
        handleContactLoginToggle={handleContactLoginToggle}
        handleAddContact={handleAddContact}
        removeContact={removeContact}
        partnerTypes={partnerTypes}
        entityTypes={entityTypes}
        sectors={sectors}
        currencies={currencies}
        branches={branches}
        taggingOptions={taggingOptions}
        assignedUserOptions={(assignedUsers || []).map(u => ({ id: u.value || u.id, name: u.label || u.name }))}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
        onConfirm={confirmDelete}
        title="Delete Client"
        message="Are you sure you want to delete this client? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        size="md"
      />
    </div>
  );
};

export default Clients;
